import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ClipboardCheck } from 'lucide-react';
import {
  consent,
  type ConsentProcessDocumentation,
  type ConsentRecordRow,
  type SubjectConsentSummary,
  type SubjectWizardState,
} from '@/api/client';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { SUBJECT_ID_LABEL } from '@/lib/subjectLabel';

/**
 * Onboarding details: what was recorded on the onboarding wizard's steps,
 * shown READ-ONLY on the subject page (client, 16 Oct 2026). Nothing here is
 * editable; corrections go through the wizard / the subject profile.
 *
 * Steps as on screen: 1 Screening, 2 Demographics, 3 Consent. The consent
 * part shows who handled consent (who obtained it, who countersigned, when
 * it was signed) and the consent-process answers (NIDCR "Documenting the
 * Consent Process") saved on the consent record. The signed documents stay
 * in Signed Consents.
 *
 * Collapsed by default, like Subject Profile. NO SUBJECT PERSONAL DATA
 * (client, 16 Oct 2026): no legal name, date of birth, signer type or
 * LAR / parent details, and so no Reveal. Those stay on the consent
 * document and in Subject Profile.
 */

interface ProcessDocRecord {
  documented_at?: string;
  consent_method?: string;
  cf_version_label?: string;
  answers?: Partial<ConsentProcessDocumentation>;
}

function parseProcessDoc(raw: string | null | undefined): ProcessDocRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ProcessDocRecord;
    return parsed && typeof parsed === 'object' && parsed.answers ? parsed : null;
  } catch {
    return null;
  }
}

const dash = <span className="text-ink-muted">—</span>;

function yesNo(v: boolean | undefined, reason?: string) {
  if (v === undefined || v === null) return dash;
  return v ? 'Yes' : `No${reason ? ` — ${reason}` : ''}`;
}

function text(v: string | number | null | undefined) {
  return v === null || v === undefined || v === '' ? dash : String(v);
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1 text-sm">
      <dt className="w-52 flex-shrink-0 text-xs font-medium text-ink-secondary">{label}</dt>
      <dd className="min-w-0 text-ink-primary">{children}</dd>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">{title}</h3>
      <dl className="divide-y divide-surface-border/60">{children}</dl>
    </div>
  );
}

export function SubjectOnboardingDetailsSection({
  subjectId,
  wizardState,
  consentSummary,
}: {
  subjectId: string;
  wizardState: SubjectWizardState | null | undefined;
  consentSummary?: SubjectConsentSummary | null;
}) {
  const [expanded, setExpanded] = useState(false);

  // Same query (and cache) as the Signed Consents section. Only fetched once
  // the card is opened.
  const consentQ = useQuery({
    queryKey: ['subject', subjectId, 'consent-records'],
    queryFn: () => consent.listRecordsForSubject(subjectId),
    enabled: !!subjectId && expanded,
  });

  const w = wizardState ?? {};
  // The most recent consent record that carries the consent-process answers.
  const withDoc = [...(consentQ.data?.records ?? [])]
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
    .map((r: ConsentRecordRow) => ({ record: r, doc: parseProcessDoc(r.process_documentation_json) }))
    .find((x) => x.doc);
  const doc = withDoc?.doc ?? null;
  const a = doc?.answers ?? {};
  const height =
    w.demographics_height_ft != null || w.demographics_height_in != null
      ? `${w.demographics_height_ft ?? 0} ft ${w.demographics_height_in ?? 0} in`
      : null;
  const language =
    a.cf_language === 'other'
      ? `Other — ${a.cf_language_other ?? ''}`
      : a.cf_language
        ? a.cf_language[0]!.toUpperCase() + a.cf_language.slice(1)
        : null;

  const cs = consentSummary ?? null;

  return (
    <section className="card p-4" data-testid="subject-onboarding-details">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        className="flex w-full flex-wrap items-center gap-2 text-left cursor-pointer"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
        )}
        <ClipboardCheck className="h-4 w-4 text-accent-600" />
        <h2 className="text-sm font-semibold text-ink-primary">Onboarding details</h2>
        <span className="text-xs text-ink-muted">
          {expanded
            ? 'Read-only, as recorded during subject onboarding.'
            : 'Click to view screening, demographics and consent details.'}
        </span>
      </div>

      {expanded && (
        <div className="mt-4 grid gap-5 lg:grid-cols-3">
          <Block title="Step 1 · Screening">
            <Row label="Screening outcome">
              {w.screening_outcome ? (w.screening_outcome === 'pass' ? 'Passed' : 'Failed') : dash}
            </Row>
            <Row label="Date screened">{w.screened_at ? fmtDateTime(w.screened_at) : dash}</Row>
            <Row label={SUBJECT_ID_LABEL}>{text(w.ivrf_number)}</Row>
          </Block>

          <Block title="Step 2 · Demographics">
            <Row label="Sex (biological)">{text(w.demographics_sex)}</Row>
            <Row label="Gender identity">{text(w.demographics_gender)}</Row>
            <Row label="Height">{text(height)}</Row>
            <Row label="Weight">{w.demographics_weight_lbs != null ? `${w.demographics_weight_lbs} lbs` : dash}</Row>
            <Row label="Ethnicity">{text(w.demographics_ethnicity)}</Row>
            <Row label="Race">{text(w.demographics_race)}</Row>
            <Row label="Preferred language">{text(w.demographics_preferred_language)}</Row>
            <Row label="Recorded">{w.demographics_recorded_at ? fmtDate(w.demographics_recorded_at) : dash}</Row>
          </Block>

          <Block title="Step 3 · Informed Consent">
            {cs ? (
              <>
                <Row label="Consent obtained by">{text(cs.obtained_by_name)}</Row>
                <Row label="Countersigned by">{text(cs.signed_by_name)}</Row>
                <Row label="Consent signed">{cs.signed_at ? fmtDateTime(cs.signed_at) : dash}</Row>
              </>
            ) : (
              <p className="py-1 text-xs text-ink-muted">No consent recorded yet.</p>
            )}

            <h4 className="mb-1 mt-3 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
              Consent process
            </h4>
            {consentQ.isError ? (
              <p className="py-1 text-xs text-ink-muted">Consent-process answers are not available to your role.</p>
            ) : !doc ? (
              <p className="py-1 text-xs text-ink-muted">
                {consentQ.isLoading ? 'Loading…' : 'No consent-process answers recorded.'}
              </p>
            ) : (
              <>
                <Row label="Documented">{doc.documented_at ? fmtDateTime(doc.documented_at) : dash}</Row>
                <Row label="Consent form version">{text(doc.cf_version_label)}</Row>
                <Row label="Subject's primary language">{text(a.subject_primary_language)}</Row>
                <Row label="Consent form language">{text(language)}</Row>
                <Row label="Interpreter">
                  {a.interpreter_available
                    ? `Yes${a.interpreter_name ? ` — ${a.interpreter_name}` : ''}`
                    : a.interpreter_available === false
                      ? 'No'
                      : dash}
                </Row>
                <Row label="Time given for questions">{yesNo(a.time_for_questions, a.time_for_questions_reason)}</Row>
                <Row label="Copy given to subject">{yesNo(a.copy_provided, a.copy_provided_reason)}</Row>
                <Row label="Signed before any procedure">
                  {yesNo(a.signed_before_procedures, a.signed_before_procedures_reason)}
                </Row>
                <Row label="Witness present">
                  {a.witness_present
                    ? `Yes${a.witness_name ? ` — ${a.witness_name}` : ''}`
                    : a.witness_present === false
                      ? 'No'
                      : dash}
                </Row>
                <Row label="Risks reviewed">{yesNo(a.risks_reviewed)}</Row>
                <Row label="Additional notes">{text(a.additional_notes)}</Row>
              </>
            )}
          </Block>
        </div>
      )}
    </section>
  );
}
